// days-until — exam countdown utility with NaN guard.
//
// E-2 (19BM · spec §3.5): "日期解析失败绝不显示 NaN 或负数".
// Previously home.tsx + course-sidebar.tsx each had a bare
//   `new Date(iso).getTime()` with no NaN check → invalid date strings
//   silently rendered NaN into the countdown UI (Math.ceil(NaN) = NaN,
//   which then slipped past `!== null` guards).
//
// Single source of truth here; both call sites import this. The NaN-check
// pattern follows date-trio.tsx (`Number.isNaN(date.getTime())`) — the one
// existing correct implementation in the codebase.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse an ISO date string safely. Returns null if invalid (never Invalid Date). */
export function parseExamDateSafe(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  // `new Date('garbage')` does not throw — it returns an Invalid Date whose
  // getTime() is NaN. This is the exact failure mode spec §3.5 targets.
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Days from now until the exam date, in local calendar days. Returns null if
 *  date invalid. Negative = past date (caller renders e.g. "考试已结束").
 *
 *  Session 3 (19BM-E audit, MED-5): changed from `Math.ceil((exam - now)/DAY)`
 *  to calendar-day comparison. The old ceil made "exam today 15:00, now 10:00"
 *  show as 1 day (ceil(5/24)=1) instead of 0 — the sidebar's `days <= 7` urgent
 *  check then misfired. Calendar-day: both normalized to local-midnight, so any
 *  time on the exam day = 0, yesterday = -1, tomorrow = +1. This matches the
 *  user's mental model of "days until the exam". */
export function daysUntil(iso: string | null | undefined): number | null {
  const exam = parseExamDateSafe(iso);
  if (!exam) return null;
  const examMidnight = startOfLocalDay(exam).getTime();
  const nowMidnight = startOfLocalDay(new Date()).getTime();
  return Math.round((examMidnight - nowMidnight) / MS_PER_DAY);
}

/** Normalize a Date to local-calendar midnight (00:00:00.000). */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
